// @flow
import EventEmitter from 'eventemitter3';
import { type DateLike } from 'app/lib/date';
import { parse as parseJobLog } from 'app/lib/job-log';

import JobLogGroupStore from 'app/stores/JobLogGroupStore';

type LogResponse = {
  streaming: boolean,
  bytes: number,
  header_times: Array<mixed>,
  output: string,
  truncated: boolean,
  started_at_from_agent?: string,
  finished_at_from_agent?: string
};

declare var jQuery: {
  ajax: (options: { url: string, success: (result: LogResponse) => mixed }) => mixed
};

export type JobPartial = {
  id: string,
  basePath: string,
  state: string,
  startedAt: DateLike
};

type Group = {
  id: string,
  name: ?string,
  expanded: boolean,
  lines: Array<{
    id: string,
    number: number,
    line: string,
    startedAt: ?DateLike
  }>,
  number: number,
  startedAt: ?DateLike,
  finishedAt: ?DateLike,
  durationPercentage: ?number,
  bootstrap?: boolean
};

export type Job = {
  id: string,
  expanded?: boolean,
  url: string,
  passed?: boolean,
  groups: Array<Group>,
  headerTimes: Array<mixed>,
  bytes: number,
  totalLines: number,
  streaming: boolean,
  loaded: boolean,
  sshKeyFailure?: boolean,
  truncated: boolean,
  startedAtFromAgent?: string,
  finishedAtFromAgent?: string,
  gitCommitNotFoundInBranchFailure?: boolean,
  linesHaveTimestamps?: boolean,
  _timeout?: ?TimeoutID
};

const createLog = (job: JobPartial): Job => ({
  id: job.id,
  url: job.basePath + "/log",
  groups: [],
  headerTimes: [],
  bytes: 0,
  totalLines: 0,
  streaming: false,
  loaded: false,
  truncated: false
});

class JobLogStore {
  eventEmitter: EventEmitter;
  state: { [string]: Job };

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {};
  }

  addChangeListener(jobId: string, callback: Function) {
    return this.eventEmitter.addListener(`${jobId}/change`, callback);
  }

  removeChangeListener(jobId: string, callback: Function) {
    return this.eventEmitter.removeListener(`${jobId}/change`, callback);
  }

  delete(jobId: string) {
    delete this.state[jobId];
    return this.eventEmitter.emit(`${jobId}/change`);
  }

  get(jobId: string) {
    return this.state[jobId];
  }

  refresh(jobId: string) {
    const log = this.get(jobId);

    return jQuery.ajax({
      url: log.url,
      success: (json: LogResponse) => {
        // Save some of log data
        log.streaming = json.streaming;
        log.bytes = json.bytes;
        log.headerTimes = json.header_times;
        log.sshKeyFailure = false;
        log.truncated = json.truncated;
        log.startedAtFromAgent = json.started_at_from_agent;
        log.finishedAtFromAgent = json.finished_at_from_agent;

        // Parse the output if we have any
        if (json.output !== "") {
          const parsed = parseJobLog(log, json.output);
          log.totalLines = parsed.totalLines;
          log.groups = parsed.groups;
          log.sshKeyFailure = parsed.sshKeyFailure;
          log.gitCommitNotFoundInBranchFailure = parsed.gitCommitNotFoundInBranchFailure;
          log.linesHaveTimestamps = parsed.linesHaveTimestamps;
        }

        // If the parser identified any groups that should be automatically
        // expanded, set the default expands for them.
        for (const group of Array.from(log.groups)) {
          JobLogGroupStore.setDefault(group.id, group.expanded);
        }

        // Mark the log as loaded
        log.loaded = true;

        // Emit the change so the components can reload
        this.eventEmitter.emit(`${log.id}/change`);

        // Reload the log if it needs to be streamed to the client
        if (log.streaming) {
          // Don't set a timeout if one has already been set
          if (log._timeout) { return; }

          // Schedule a refresh of the log in 5 seconds
          return log._timeout = setTimeout(
            () => {
              this.refresh(log.id);
              return log._timeout = null;
            },
            5000
          );
        }
      }
    });
  }

  load(job: JobPartial) {
    // Find or create the log
    const { id } = job;

    // Create the log record if it doesn't exist, and refresh it's contents
    if (!this.state[id]) {
      this.state[id] = createLog(job);
      this.refresh(id);
    }

    return this.state[id];
  }
}

export default new JobLogStore();
